import { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { type RewindsStillStoryboard } from 'utils/rewinds/previewStills';
import styles from './CamPlayerPreviewImage.module.scss';

const StoryboardImage = ({ storyboard }: { storyboard: RewindsStillStoryboard | null }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(!!storyboard?.hasLoaded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyboard?.storyboardUrl]);

  const onLoad = useCallback(() => {
    setLoaded(true);

    if (storyboard) storyboard.hasLoaded = true;
    // cant add the whole storyboard object to the dependency array because it causes excessive re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyboard?.storyboardUrl]);

  const style: React.CSSProperties = useMemo(() => {
    if (!storyboard) return {};

    // calculate the offset and zoom required to show the correct part of the storyboard image
    const { column, row, storyboardWidth, thumbnailWidth } = storyboard;
    const zoom = storyboardWidth / thumbnailWidth;
    const topOffset = row * 100;
    const leftOffset = column * 100;
    return {
      top: `-${topOffset}%`,
      left: `-${leftOffset}%`,
      objectFit: 'fill',
      width: `100%`,
      height: `100%`,
      transform: `scale(${zoom})`,
      transformOrigin: 'top left',
      position: 'absolute',
    };
  }, [storyboard]);

  return storyboard?.storyboardUrl ? (
    // we want the CDN to serve this image for performance reasons and
    // next.js image will serve it from the kbyg-web server.
    // eslint-disable-next-line @next/next/no-img-element
    <img
      alt="Storyboard Image"
      className={classNames(styles.content, { [styles.hidden]: !loaded })}
      data-testid="storyboard-image"
      onLoad={onLoad}
      src={storyboard.storyboardUrl}
      style={style}
    />
  ) : null;
};

export default StoryboardImage;
